import type { AlignYToken, BorderRadiusToken, ColorToken, SpacingToken } from './types';

/**
 * Raw resolved color values, keyed by the structured-content color name.
 *
 * Values are the literal hex the Panda tokens resolve to — NOT the runtime `token.var()`
 * helper — so the `remove-unused-css-vars` Panda hook can safely prune CSS variables that
 * are only referenced at runtime (these are applied via inline-style custom properties).
 */
export const COLORS_MAP: Record<ColorToken, string> = {
  transparent: 'transparent',

  // Brand Colors
  green: '#008254',
  'green-light': '#E3FAF2',
  'green-lighter': '#B7ECDA',
  'green-lighter-2': '#B7ECDA',
  'green-lightest': '#E3FAF2',
  'green-extra-light': '#EFFCF8',
  'green-dark': '#006642',
  'green-darkest': '#00482F',
  'nw-green': '#8ccb32',
  'nw-logo-green': '#00AE4D',
  'nw-logo-green-dark': '#00682C',
  'nw-logo-green-light': '#8DC63F',

  blue: '#005FB9',
  'blue-light': '#54A0E8',
  'blue-lighter': '#9DCDFA',
  'blue-lighter-2': '#9DCDFA',
  'blue-lightest': '#EEF7FF',
  'blue-extra-light': '#DEEFFF',
  'blue-dark': '#02427E',
  'blue-darkest': '#02143A',

  red: '#DE2828',
  'red-light': '#FF5D5E',
  'red-lighter': '#FF9090',
  'red-lighter-2': '#FFBFBF',
  'red-lightest': '#FFE8E8',
  'red-dark': '#B40000',
  'red-darker': '#B40000',
  'red-extra-light': '#FFE8E8',

  yellow: '#FFC764',
  'yellow-light': '#FBD083',
  'yellow-lighter': '#FFE2AE',
  'yellow-lighter-2': '#FFF0D6',
  'yellow-lightest': '#FFF7EA',
  'yellow-extra-light': '#FFFBF4',
  'yellow-dark': '#EFA92E',
  'yellow-darkest': '#B07000',

  white: '#FFFFFF',
  'neutral-darkest': '#2A2A2A',
  'neutral-darker': '#3D4045',
  'neutral-dark': '#64666A',
  'neutral-light': '#B1B3B5',
  'neutral-lighter': '#D8D9DA',
  'neutral-lighter-2': '#EBEBEC',
  'neutral-lightest': '#F5F5F5',
  'neutral-darker-2': '#3D4045',
  'neutral-extra-dark': '#2A2A2A',

  brown: '#B96C4F',
  'brown-light': '#D58B70',
  'brown-lighter': '#EDBEAD',
  'brown-lighter-2': '#FFEAE2',
  'brown-lighter-3': '#FEF4F1',
  'brown-lighter-4': '#FFFAF9',
  'brown-lightest': '#FEF4F1',
  'brown-extra-light': '#FFFAF9',
  'brown-dark': '#91472B',
  'brown-darkest': '#652812',

  bone: '#F4EEE6',

  teal: '#005FB9',
  'teal-light': '#EEF7FF',
};

export const SPACING_TOKEN_MAP: Record<SpacingToken, string> = {
  '0': '0',
  '1': '0.25rem',
  '2': '0.5rem',
  '3': '1rem',
  '3-half': '1.5rem',
  '4': '1.75rem',
  '5': '2.75rem',
  '0--wide': '0',
  '1--wide': '0.25rem',
  '2--wide': '0.5rem',
  '3--wide': '1rem',
  '3-half--wide': '1.5rem',
  '4--wide': '1.75rem',
  '5--wide': '2.75rem',
};

export const ALIGN_Y_MAP: Record<AlignYToken, 'flex-start' | 'center' | 'flex-end'> = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
};

export const BORDER_RADIUS_MAP: Record<BorderRadiusToken, string> = {
  0: '0',
  1: '0.125rem',
  2: '0.25rem',
} as const;
